import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
    IAumicoCompanySummary,
    ICompanyMappingSuggestion,
} from '../../models/integration/company-mapping-suggestion.model';
import { IAumicoCompanyWithMapping } from '../../models/integration/connection-mapping.model';
import { ConnectionStatus } from '../../models/integration/connection-status.enum';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { IExternalCompaniesState } from '../../reducer/integration.reducer';
import { IntegrationProvider } from '../../provider/integration.provider';

/**
 * Manage modal for Proffix servers. Cloned from the D365 BC manage modal (Proffix shares
 * the auto-match mapping flow). Three internal views (`ManageView`):
 *
 *  1. `list`               : all connected Proffix servers for this org with "+ Server" CTA.
 *  2. `detail`             : drill into one server; lists Proffix databases (Mandanten)
 *                            enriched with their mapped aumico Company, Edit / Unlink per
 *                            row, Disconnect at server scope.
 *  3. `confirm_disconnect` : two-stage confirm before disconnect.
 *
 * Server identity is the normalized serverUrl origin — Proffix has no tenant UUID.
 */
type ManageView = 'list' | 'detail' | 'confirm_disconnect';

interface IServerRow {
    connection: IIntegrationConnection;
    needsReAuth: boolean;
}

interface IDetailRow {
    suggestion: ICompanyMappingSuggestion;
    mappedAumicoCompany: IAumicoCompanySummary | null;
    mappedAumicoCompanyId: string | null;
}

@Component({
    selector: 'aumico-proffix-manage-modal',
    templateUrl: './proffix-manage-modal.component.html',
    styleUrls: ['./proffix-manage-modal.component.scss'],
})
export class ProffixManageModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();
    /** Emitted when the user clicks "+ Neuen Server verbinden" inside the list view. */
    @Output() public requestAddServer = new EventEmitter<void>();

    /** When set on open, drill directly into the detail view for this connection. */
    @Input() public initialConnectionId: string | null = null;

    public view: ManageView = 'list';
    public servers$!: Observable<IServerRow[]>;
    public selectedConnectionId: string | null = null;
    public selectedServer$: Observable<IServerRow | null> | null = null;
    public externalCompanies$!: Observable<ICompanyMappingSuggestion[]>;
    public externalCompaniesState$!: Observable<IExternalCompaniesState>;
    public mappedCompanies$!: Observable<IAumicoCompanyWithMapping[]>;
    public autoMatchBanner$: Observable<
        | null
        | { kind: 'running' }
        | { kind: 'error'; message: string }
        | { kind: 'success'; autoMatched: number; notMapped: number; errors: number }
    > | null = null;

    public disconnectCandidate: IIntegrationConnection | null = null;

    public readonly searchTerm$ = new BehaviorSubject<string>('');
    public detailRows$!: Observable<IDetailRow[]>;

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.servers$ = this.integrationProvider
            .getConnectionsByVendor$(IntegrationVendor.PROFFIX)
            .pipe(
                map((connections) =>
                    connections.map((connection) => ({
                        connection,
                        needsReAuth:
                            connection.status === ConnectionStatus.EXPIRED ||
                            connection.status === ConnectionStatus.ERROR,
                    })),
                ),
            );

        this.externalCompanies$ = this.integrationProvider.getExternalCompanies$();
        this.externalCompaniesState$ = this.integrationProvider.getExternalCompaniesState$();
        this.mappedCompanies$ = this.integrationProvider.getTenantDetailCompanies$();

        this.detailRows$ = combineLatest([
            this.externalCompaniesState$,
            this.mappedCompanies$,
            this.searchTerm$,
        ]).pipe(
            map(([external, mapped, term]) => {
                if (external.connectionId !== this.selectedConnectionId) return [];
                const byExternalId = new Map(
                    mapped.map((c) => [c.mapping.externalCompanyId, c]),
                );
                const enriched: IDetailRow[] = external.items.map((s) => {
                    const m = byExternalId.get(s.vendorCompany.externalCompanyId);
                    const mappedAumicoCompany: IAumicoCompanySummary | null = m
                        ? { id: m.id, name: m.name }
                        : null;
                    const decoratedSuggestion: ICompanyMappingSuggestion = {
                        ...s,
                        suggestedAumicoCompany: mappedAumicoCompany ?? s.suggestedAumicoCompany,
                    };
                    return {
                        suggestion: decoratedSuggestion,
                        mappedAumicoCompany,
                        mappedAumicoCompanyId: m?.id ?? null,
                    };
                });
                const trimmed = term.trim().toLowerCase();
                if (!trimmed) return enriched;
                return enriched.filter((row) =>
                    row.suggestion.vendorCompany.externalCompanyName
                        .toLowerCase()
                        .includes(trimmed),
                );
            }),
        );

        this.servers$.pipe(takeUntil(this.destroy$)).subscribe((servers) => {
            if (this.view === 'detail' && this.selectedConnectionId != null) {
                const stillExists = servers.some(
                    (t) => t.connection.id === this.selectedConnectionId,
                );
                if (!stillExists) {
                    this.view = 'list';
                    this.selectedConnectionId = null;
                    this.selectedServer$ = null;
                }
                return;
            }
            if (
                this.initialConnectionId != null &&
                this.view === 'list' &&
                this.selectedConnectionId == null
            ) {
                const target = servers.find(
                    (t) => t.connection.id === this.initialConnectionId,
                );
                if (target) this.onSelectServer(target);
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchTerm$.complete();
    }

    public onSelectServer(row: IServerRow): void {
        this.selectedConnectionId = row.connection.id;
        this.selectedServer$ = this.servers$.pipe(
            map((rows) => rows.find((r) => r.connection.id === row.connection.id) ?? null),
        );
        this.autoMatchBanner$ = this.integrationProvider.getAutoMatchBanner$(row.connection.id);
        this.view = 'detail';
        this.searchTerm$.next('');
        this.integrationProvider.loadExternalCompanies(row.connection.id);
        this.integrationProvider.loadMappedCompanies(row.connection.id);
    }

    public onBackToList(): void {
        this.view = 'list';
        this.selectedConnectionId = null;
        this.selectedServer$ = null;
        this.searchTerm$.next('');
    }

    public onAddServer(): void {
        this.requestAddServer.emit();
    }

    public onEditSingleCompany(row: IDetailRow): void {
        if (this.selectedConnectionId != null) {
            this.integrationProvider.startSingleEdit(
                this.selectedConnectionId,
                IntegrationVendor.PROFFIX,
                row.suggestion,
            );
        }
    }

    public onUnlinkCompany(row: IDetailRow): void {
        if (row.mappedAumicoCompanyId != null) {
            this.integrationProvider.deleteMapping(
                row.mappedAumicoCompanyId,
                IntegrationVendor.PROFFIX,
            );
        }
    }

    public onSearchTermChange(term: string): void {
        this.searchTerm$.next(term);
    }

    public onRequestDisconnect(connection: IIntegrationConnection): void {
        this.disconnectCandidate = connection;
        this.view = 'confirm_disconnect';
    }

    public onCancelDisconnect(): void {
        this.disconnectCandidate = null;
        this.view = this.selectedConnectionId != null ? 'detail' : 'list';
    }

    public onConfirmDisconnect(): void {
        if (this.disconnectCandidate == null) return;
        const id = this.disconnectCandidate.id;
        this.integrationProvider.disconnect(id);
        this.disconnectCandidate = null;
        this.view = 'list';
        if (this.selectedConnectionId === id) {
            this.selectedConnectionId = null;
            this.selectedServer$ = null;
        }
    }

    public onClose(): void {
        this.closed.emit();
    }

    public trackByConnectionId(_: number, row: IServerRow): string {
        return row.connection.id;
    }

    public trackByDetailRow(_: number, row: IDetailRow): string {
        return row.suggestion.vendorCompany.externalCompanyId;
    }
}
